import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import AsyncSelect from 'react-select/async';
import {
  fetchProjectReport,
  createProjectReport,
  updateProjectReport,
  fetchCustomers,
  createCustomer,
  fetchSectors,
  createSector,
  fetchTechnologies,
  createTechnology,
  fetchCompetenceFields,
  createCompetenceField,
  fetchSkills,
} from '../api/projectReports';
import type {
  ProjectReport,
  ProjectReportPayload,
  Customer,
  Sector,
  Technology,
  CompetenceField,
  Skill,
} from '../api/projectReports';
import apiClient from '../api/client';
import RichTextEditor from '../components/RichTextEditor';

interface SelectOption {
  value: string;
  label: string;
}

const CONTRACT_TYPE_VALUES = [
  'WERKVERTRAG',
  'ARBEITNEHMERUEBERLASSUNG',
  'DIENSTVERTRAG',
  'RAHMENVERTRAG',
] as const;

export default function ProjectReportForm() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  // Form state
  const [projectId, setProjectId] = useState('');
  const [title, setTitle] = useState('');
  const [authorIds, setAuthorIds] = useState<SelectOption[]>([]);
  const [customerId, setCustomerId] = useState<SelectOption | null>(null);
  const [department, setDepartment] = useState('');
  const [competenceFieldIds, setCompetenceFieldIds] = useState<SelectOption[]>([]);
  const [contractType, setContractType] = useState<SelectOption | null>(null);
  const [sectorIds, setSectorIds] = useState<SelectOption[]>([]);
  const [technologyIds, setTechnologyIds] = useState<SelectOption[]>([]);
  const [skillIds, setSkillIds] = useState<SelectOption[]>([]);
  const [projectStart, setProjectStart] = useState('');
  const [projectEnd, setProjectEnd] = useState('');
  const [content, setContent] = useState('');
  const [challenges, setChallenges] = useState('');
  const [nextSteps, setNextSteps] = useState('');
  const [potentials, setPotentials] = useState('');
  const [lessonsLearned, setLessonsLearned] = useState('');

  // Reference data
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [technologies, setTechnologies] = useState<Technology[]>([]);
  const [competenceFields, setCompetenceFields] = useState<CompetenceField[]>([]);
  const [availableSkills, setAvailableSkills] = useState<Skill[]>([]);

  // UI state
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load reference data
  useEffect(() => {
    Promise.all([fetchCustomers(), fetchSectors(), fetchTechnologies(), fetchCompetenceFields(), fetchSkills()])
      .then(([c, s, tech, cf, sk]) => {
        setCustomers(c);
        setSectors(s);
        setTechnologies(tech);
        setCompetenceFields(cf);
        setAvailableSkills(sk);
      })
      .catch(() => { /* ignored — selects will just be empty */ });
  }, []);

  // Load existing report for editing
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    fetchProjectReport(id)
      .then((report: ProjectReport) => {
        setProjectId(report.project_id);
        setTitle(report.title);
        setAuthorIds(
          (report.authors ?? []).map((a) => ({ value: a.id, label: a.full_name })),
        );
        if (report.customer_id && report.customer_name) {
          setCustomerId({ value: report.customer_id, label: report.customer_name });
        }
        setDepartment(report.department ?? '');
        setCompetenceFieldIds(
          (report.competence_field_ids ?? []).map((cfid, i) => ({
            value: cfid,
            label: report.competence_field_names?.[i] ?? cfid,
          })),
        );
        if (report.contract_type) {
          setContractType({
            value: report.contract_type,
            label: t(`projectReports.detail.contractTypes.${report.contract_type}`, { defaultValue: report.contract_type }),
          });
        }
        setSectorIds(
          (report.sector_ids ?? []).map((sid, i) => ({
            value: sid,
            label: report.sector_names?.[i] ?? sid,
          })),
        );
        setTechnologyIds(
          (report.technology_ids ?? []).map((tid, i) => ({
            value: tid,
            label: report.technology_names?.[i] ?? tid,
          })),
        );
        setSkillIds(
          (report.skill_ids ?? []).map((sid, i) => ({
            value: sid,
            label: report.skill_names?.[i] ?? sid,
          })),
        );
        setProjectStart(report.project_start ?? '');
        setProjectEnd(report.project_end ?? '');
        setContent(report.content ?? '');
        setChallenges(report.challenges ?? '');
        setNextSteps(report.next_steps ?? '');
        setPotentials(report.potentials ?? '');
        setLessonsLearned(report.lessons_learned ?? '');
      })
      .catch(() => { /* handled by loading state */ })
      .finally(() => setLoading(false));
  }, [id, t]);

  // Load employees for author async select
  const loadEmployeeOptions = useCallback(
    async (inputValue: string): Promise<SelectOption[]> => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data } = await apiClient.get<any>('/employees', {
          params: { q: inputValue || undefined, size: 50 },
        });
        const items = data.content ?? [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return items.map((emp: any) => ({
          value: emp.id,
          label: emp.full_name,
        }));
      } catch {
        return [];
      }
    },
    [],
  );

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!projectId.trim()) errs.projectId = t('projectReports.form.validation.projectIdRequired');
    if (!title.trim()) errs.title = t('projectReports.form.validation.titleRequired');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    const payload: ProjectReportPayload = {
      project_id: projectId.trim(),
      title: title.trim(),
      author_ids: authorIds.map((a) => a.value),
      customer_id: customerId?.value ?? null,
      department: department.trim() || null,
      competence_field_ids: competenceFieldIds.map((cf) => cf.value),
      contract_type: contractType?.value ?? null,
      sector_ids: sectorIds.map((s) => s.value),
      technology_ids: technologyIds.map((tech) => tech.value),
      skill_ids: skillIds.map((s) => s.value),
      project_start: projectStart || null,
      project_end: projectEnd || null,
      content: content || null,
      challenges: challenges || null,
      next_steps: nextSteps || null,
      potentials: potentials || null,
      lessons_learned: lessonsLearned || null,
    };

    try {
      let saved: ProjectReport;
      if (isEdit && id) {
        saved = await updateProjectReport(id, payload);
      } else {
        saved = await createProjectReport(payload);
      }
      navigate(`/project-reports/${saved.id}`);
    } catch {
      setErrors({ form: t('projectReports.failedToLoad') });
    } finally {
      setSaving(false);
    }
  }

  // Creatable handlers
  async function handleCreateCustomer(inputValue: string) {
    const created = await createCustomer(inputValue);
    setCustomers((prev) => [...prev, created]);
    setCustomerId({ value: created.id, label: created.title });
  }

  async function handleCreateSector(inputValue: string) {
    const created = await createSector(inputValue);
    setSectors((prev) => [...prev, created]);
    setSectorIds((prev) => [...prev, { value: created.id, label: created.title }]);
  }

  async function handleCreateTechnology(inputValue: string) {
    const created = await createTechnology(inputValue);
    setTechnologies((prev) => [...prev, created]);
    setTechnologyIds((prev) => [...prev, { value: created.id, label: created.title }]);
  }

  async function handleCreateCompetenceField(inputValue: string) {
    const created = await createCompetenceField(inputValue);
    setCompetenceFields((prev) => [...prev, created]);
    setCompetenceFieldIds((prev) => [...prev, { value: created.id, label: created.title }]);
  }

  const contractTypeOptions: SelectOption[] = CONTRACT_TYPE_VALUES.map((ct) => ({
    value: ct,
    label: t(`projectReports.detail.contractTypes.${ct}`, { defaultValue: ct }),
  }));

  const customerOptions: SelectOption[] = customers.map((c) => ({
    value: c.id,
    label: c.title,
  }));

  const sectorOptions: SelectOption[] = sectors.map((s) => ({
    value: s.id,
    label: s.title,
  }));

  const technologyOptions: SelectOption[] = technologies.map((tech) => ({
    value: tech.id,
    label: tech.title,
  }));

  const competenceFieldOptions: SelectOption[] = competenceFields.map((cf) => ({
    value: cf.id,
    label: cf.title,
  }));

  const skillOptions: SelectOption[] = availableSkills.map((s) => ({
    value: s.id,
    label: s.name,
  }));

  if (loading) {
    return (
      <div role="status" aria-live="polite" className="flex items-center gap-3 text-gray-500 py-8">
        <span className="animate-spin text-brand-600 text-xl" aria-hidden="true">&#x27F3;</span>
        {t('projectReports.loading')}
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <header>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? t('projectReports.form.editTitle') : t('projectReports.form.createTitle')}
        </h1>
      </header>

      {errors.form && (
        <div role="alert" className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700 text-sm">
          {errors.form}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="card space-y-4">
          <div>
            <label htmlFor="projectId" className="form-label">{t('projectReports.form.projectId')} *</label>
            <input
              id="projectId"
              type="text"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className={`form-input w-full ${errors.projectId ? 'border-red-500' : ''}`}
            />
            {errors.projectId && <p className="text-xs text-red-600 mt-1">{errors.projectId}</p>}
          </div>

          <div>
            <label htmlFor="title" className="form-label">{t('projectReports.form.title')} *</label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={`form-input w-full ${errors.title ? 'border-red-500' : ''}`}
            />
            {errors.title && <p className="text-xs text-red-600 mt-1">{errors.title}</p>}
          </div>

          <div>
            <label className="form-label">{t('projectReports.form.authors')}</label>
            <AsyncSelect<SelectOption, true>
              isMulti
              cacheOptions
              defaultOptions
              loadOptions={loadEmployeeOptions}
              value={authorIds}
              onChange={(val) => setAuthorIds([...(val ?? [])])}
              placeholder={t('projectReports.form.selectAuthors')}
              classNamePrefix="react-select"
            />
          </div>

          <div>
            <label className="form-label">{t('projectReports.form.customer')}</label>
            <CreatableSelect<SelectOption>
              isClearable
              options={customerOptions}
              value={customerId}
              onChange={(val) => setCustomerId(val)}
              onCreateOption={handleCreateCustomer}
              placeholder={t('projectReports.form.selectOrCreateCustomer')}
              classNamePrefix="react-select"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="department" className="form-label">{t('projectReports.form.department')}</label>
              <input
                id="department"
                type="text"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                className="form-input w-full"
              />
            </div>
            <div>
              <label className="form-label">{t('projectReports.form.competenceField')}</label>
              <CreatableSelect<SelectOption, true>
                isMulti
                options={competenceFieldOptions}
                value={competenceFieldIds}
                onChange={(val) => setCompetenceFieldIds([...(val ?? [])])}
                onCreateOption={handleCreateCompetenceField}
                placeholder={t('projectReports.form.selectOrCreateCompetenceFields')}
                classNamePrefix="react-select"
              />
            </div>
          </div>

          <div>
            <label className="form-label">{t('projectReports.form.contractType')}</label>
            <Select<SelectOption>
              isClearable
              options={contractTypeOptions}
              value={contractType}
              onChange={(val) => setContractType(val)}
              placeholder={t('projectReports.form.selectContractType')}
              classNamePrefix="react-select"
            />
          </div>
        </div>

        {/* Tags */}
        <div className="card space-y-4">
          <div>
            <label className="form-label">{t('projectReports.form.sectors')}</label>
            <CreatableSelect<SelectOption, true>
              isMulti
              options={sectorOptions}
              value={sectorIds}
              onChange={(val) => setSectorIds([...(val ?? [])])}
              onCreateOption={handleCreateSector}
              placeholder={t('projectReports.form.selectOrCreateSectors')}
              classNamePrefix="react-select"
            />
          </div>

          <div>
            <label className="form-label">{t('projectReports.form.technologies')}</label>
            <CreatableSelect<SelectOption, true>
              isMulti
              options={technologyOptions}
              value={technologyIds}
              onChange={(val) => setTechnologyIds([...(val ?? [])])}
              onCreateOption={handleCreateTechnology}
              placeholder={t('projectReports.form.selectOrCreateTechnologies')}
              classNamePrefix="react-select"
            />
          </div>

          <div>
            <label className="form-label">{t('projectReports.form.skills')}</label>
            <Select<SelectOption, true>
              isMulti
              options={skillOptions}
              value={skillIds}
              onChange={(val) => setSkillIds([...(val ?? [])])}
              placeholder={t('projectReports.form.selectOrCreateSkills')}
              classNamePrefix="react-select"
            />
          </div>
        </div>

        {/* Dates */}
        <div className="card">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="projectStart" className="form-label">{t('projectReports.form.projectStart')}</label>
              <input
                id="projectStart"
                type="date"
                value={projectStart}
                onChange={(e) => setProjectStart(e.target.value)}
                className="form-input w-full"
              />
            </div>
            <div>
              <label htmlFor="projectEnd" className="form-label">{t('projectReports.form.projectEnd')}</label>
              <input
                id="projectEnd"
                type="date"
                value={projectEnd}
                onChange={(e) => setProjectEnd(e.target.value)}
                className="form-input w-full"
              />
            </div>
          </div>
        </div>

        {/* Rich text fields */}
        <div className="card space-y-6">
          <div>
            <label className="form-label">{t('projectReports.form.content')}</label>
            <RichTextEditor content={content} onChange={setContent} />
          </div>

          <div>
            <label className="form-label">{t('projectReports.form.challenges')}</label>
            <RichTextEditor content={challenges} onChange={setChallenges} />
          </div>

          <div>
            <label className="form-label">{t('projectReports.form.nextSteps')}</label>
            <RichTextEditor content={nextSteps} onChange={setNextSteps} />
          </div>

          <div>
            <label className="form-label">{t('projectReports.form.potentials')}</label>
            <RichTextEditor content={potentials} onChange={setPotentials} />
          </div>

          <div>
            <label className="form-label">{t('projectReports.form.lessonsLearned')}</label>
            <RichTextEditor content={lessonsLearned} onChange={setLessonsLearned} />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            className="btn-primary text-sm"
            aria-busy={saving}
          >
            {saving ? t('projectReports.form.saving') : t('projectReports.form.save')}
          </button>
          <button
            type="button"
            onClick={() => navigate(isEdit && id ? `/project-reports/${id}` : '/project-reports')}
            className="btn-secondary text-sm"
          >
            {t('projectReports.form.cancel')}
          </button>
        </div>
      </form>
    </div>
  );
}
